/**
 * ConditionsBuilder — UI pra editar conditions de uma automação (FEAT-V2-032).
 *
 * Toggle AND/OR + lista de rules (sem aninhar grupos). Cada rule:
 *   [Campo ▾] [Operador ▾] [Valor input dinâmico]  [×]
 *
 * Operadores filtrados pelo fieldType do campo. Valor é input dinâmico:
 *   - text/number → Input
 *   - select → Select com options do campo
 *   - tags → TagPicker multi-select de tagIds
 *   - boolean → checkbox
 *   - date → date input
 */

import { useMemo } from 'react';

import { Button, Input, Select, SelectOption, Label } from '@/components/ui';
import { TagBadge } from '@/components/tags';
import { useCustomFields, type CustomFieldType } from '@/lib/custom-fields-store';
import { useTagsCatalog } from '@/lib/tags-store';

// ─── Tipos espelhados do backend ────────────────────────────────────────

export type Operator =
  | '='
  | '!='
  | 'contains'
  | 'starts_with'
  | 'ends_with'
  | '>'
  | '>='
  | '<'
  | '<='
  | 'is_empty'
  | 'is_filled'
  | 'tag_in';

export interface Rule {
  field: string;
  operator: Operator;
  value: unknown;
  fieldType: CustomFieldType;
}

export interface ConditionsConfig {
  logic: 'AND' | 'OR';
  rules: Rule[];
}

const OPERATORS_BY_TYPE: Record<CustomFieldType, Operator[]> = {
  text: ['=', '!=', 'contains', 'starts_with', 'ends_with', 'is_empty', 'is_filled'],
  number: ['=', '!=', '>', '>=', '<', '<=', 'is_empty', 'is_filled'],
  select: ['=', '!=', 'is_empty', 'is_filled'],
  multiselect: ['contains', 'is_empty', 'is_filled'],
  tags: ['tag_in', 'is_empty', 'is_filled'],
  boolean: ['=', '!='],
  date: ['=', '!=', '>', '>=', '<', '<=', 'is_empty', 'is_filled'],
};

const OPERATOR_LABELS: Record<Operator, string> = {
  '=': 'é igual a',
  '!=': 'é diferente de',
  contains: 'contém',
  starts_with: 'começa com',
  ends_with: 'termina com',
  '>': 'é maior que',
  '>=': 'é maior ou igual a',
  '<': 'é menor que',
  '<=': 'é menor ou igual a',
  is_empty: 'está vazio',
  is_filled: 'está preenchido',
  tag_in: 'inclui alguma de',
};

interface ConditionsBuilderProps {
  value: ConditionsConfig | null;
  onChange: (next: ConditionsConfig | null) => void;
  workflowSlug: string;
}

export function ConditionsBuilder({
  value,
  onChange,
  workflowSlug,
}: ConditionsBuilderProps) {
  const { data: fields = [] } = useCustomFields(workflowSlug);
  const conditions = value ?? { logic: 'AND', rules: [] };

  function update(next: ConditionsConfig) {
    if (next.rules.length === 0) {
      onChange(null);
      return;
    }
    onChange(next);
  }

  function addRule() {
    const defaultField = fields[0];
    if (!defaultField) return;
    const newRule: Rule = {
      field: defaultField.field,
      operator: OPERATORS_BY_TYPE[defaultField.type][0],
      value: defaultValueForType(defaultField.type),
      fieldType: defaultField.type,
    };
    update({ ...conditions, rules: [...conditions.rules, newRule] });
  }

  function removeRule(idx: number) {
    update({ ...conditions, rules: conditions.rules.filter((_, i) => i !== idx) });
  }

  function updateRule(idx: number, patch: Partial<Rule>) {
    update({
      ...conditions,
      rules: conditions.rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    });
  }

  function toggleLogic() {
    update({ ...conditions, logic: conditions.logic === 'AND' ? 'OR' : 'AND' });
  }

  return (
    <div data-testid="conditions-builder" className="border border-line dark:border-line-dark rounded-xl p-3 space-y-2">
      <div className="flex items-center justify-between">
        <Label>Condições (opcional)</Label>
        {conditions.rules.length > 1 && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-ink-3">Combinar com:</span>
            <button
              type="button"
              data-testid="conditions-logic-toggle"
              onClick={toggleLogic}
              className="px-2 py-0.5 rounded border border-line dark:border-line-dark text-ink-1 hover:bg-paper-warm dark:hover:bg-paper-dark-warm transition-colors"
            >
              {conditions.logic === 'AND' ? 'E (todas)' : 'OU (qualquer)'}
            </button>
          </div>
        )}
      </div>

      {conditions.rules.length === 0 ? (
        <p className="text-xs text-ink-3 italic">
          Sem condições — a automação executa toda vez que o trigger acontece.
        </p>
      ) : (
        <ul className="space-y-2">
          {conditions.rules.map((rule, idx) => (
            <li
              key={idx}
              data-testid={`conditions-rule-${idx}`}
              className="flex flex-wrap items-center gap-2 p-2 rounded bg-paper-warm dark:bg-paper-dark-warm"
            >
              <FieldSelect
                fields={fields}
                value={rule.field}
                onChange={(field, type) => {
                  const ops = OPERATORS_BY_TYPE[type];
                  // Se operator atual não vale pro novo type, escolhe o 1º válido.
                  const op = ops.includes(rule.operator) ? rule.operator : ops[0];
                  updateRule(idx, {
                    field,
                    fieldType: type,
                    operator: op,
                    value: defaultValueForType(type),
                  });
                }}
                testId={`conditions-rule-${idx}-field`}
              />
              <OperatorSelect
                fieldType={rule.fieldType}
                value={rule.operator}
                onChange={(op) => updateRule(idx, { operator: op })}
                testId={`conditions-rule-${idx}-operator`}
              />
              {requiresValueInput(rule.operator) && (
                <ValueInput
                  rule={rule}
                  fields={fields}
                  onChange={(v) => updateRule(idx, { value: v })}
                  testId={`conditions-rule-${idx}-value`}
                />
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeRule(idx)}
                data-testid={`conditions-rule-${idx}-remove`}
                aria-label="Remover condição"
              >
                ✕
              </Button>
            </li>
          ))}
        </ul>
      )}

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={addRule}
        data-testid="conditions-add-rule"
        disabled={fields.length === 0}
      >
        + Adicionar condição
      </Button>
    </div>
  );
}

function requiresValueInput(op: Operator): boolean {
  return op !== 'is_empty' && op !== 'is_filled';
}

function defaultValueForType(type: CustomFieldType): unknown {
  if (type === 'number') return 0;
  if (type === 'boolean') return false;
  if (type === 'multiselect' || type === 'tags') return [];
  if (type === 'date') return '';
  return '';
}

// ─── Subcomponents ──────────────────────────────────────────────────────

function FieldSelect({
  fields,
  value,
  onChange,
  testId,
}: {
  fields: Array<{ field: string; label: string; type: CustomFieldType }>;
  value: string;
  onChange: (field: string, type: CustomFieldType) => void;
  testId: string;
}) {
  return (
    <Select
      value={value}
      onChange={(e) => {
        const selected = fields.find((f) => f.field === e.target.value);
        if (selected) onChange(selected.field, selected.type);
      }}
      data-testid={testId}
      className="text-xs"
    >
      {fields.map((f) => (
        <SelectOption key={f.field} value={f.field}>
          {f.label}
        </SelectOption>
      ))}
    </Select>
  );
}

function OperatorSelect({
  fieldType,
  value,
  onChange,
  testId,
}: {
  fieldType: CustomFieldType;
  value: Operator;
  onChange: (op: Operator) => void;
  testId: string;
}) {
  const ops = OPERATORS_BY_TYPE[fieldType] ?? [];
  return (
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value as Operator)}
      data-testid={testId}
      className="text-xs"
    >
      {ops.map((op) => (
        <SelectOption key={op} value={op}>
          {OPERATOR_LABELS[op]}
        </SelectOption>
      ))}
    </Select>
  );
}

function ValueInput({
  rule,
  fields,
  onChange,
  testId,
}: {
  rule: Rule;
  fields: Array<{ field: string; label: string; type: CustomFieldType; options?: Array<{ value: string; label: string }> }>;
  onChange: (v: unknown) => void;
  testId: string;
}) {
  const fieldDef = fields.find((f) => f.field === rule.field);

  if (rule.fieldType === 'boolean') {
    return (
      <label className="flex items-center gap-1 text-xs">
        <input
          type="checkbox"
          checked={!!rule.value}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={testId}
        />
        Verdadeiro
      </label>
    );
  }

  if (rule.fieldType === 'select' && fieldDef?.options) {
    return (
      <Select
        value={typeof rule.value === 'string' ? rule.value : ''}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="text-xs"
      >
        <SelectOption value="">(escolha…)</SelectOption>
        {fieldDef.options.map((o) => (
          <SelectOption key={o.value} value={o.value}>
            {o.label}
          </SelectOption>
        ))}
      </Select>
    );
  }

  if (rule.fieldType === 'tags') {
    return <TagsValuePicker value={rule.value} onChange={onChange} testId={testId} />;
  }

  if (rule.fieldType === 'number') {
    return (
      <Input
        type="number"
        value={typeof rule.value === 'number' ? rule.value : ''}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        data-testid={testId}
        className="text-xs w-32"
      />
    );
  }

  if (rule.fieldType === 'date') {
    return (
      <Input
        type="date"
        value={typeof rule.value === 'string' ? rule.value : ''}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="text-xs w-40"
      />
    );
  }

  // text / multiselect (fallback string)
  return (
    <Input
      type="text"
      value={typeof rule.value === 'string' ? rule.value : ''}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="text-xs"
      placeholder="valor"
    />
  );
}

function TagsValuePicker({
  value,
  onChange,
  testId,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  testId: string;
}) {
  const { data: catalog = [] } = useTagsCatalog();
  const selectedIds = useMemo(
    () =>
      Array.isArray(value)
        ? (value as unknown[]).filter((v): v is string => typeof v === 'string')
        : [],
    [value],
  );
  const selectedTags = useMemo(
    () => catalog.filter((t) => selectedIds.includes(t.id)),
    [catalog, selectedIds],
  );

  function toggle(id: string) {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((s) => s !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  }

  return (
    <div data-testid={testId} className="flex items-center gap-2 flex-wrap">
      <Select
        value=""
        onChange={(e) => {
          if (e.target.value) toggle(e.target.value);
        }}
        className="text-xs"
      >
        <SelectOption value="">+ tag</SelectOption>
        {catalog
          .filter((t) => !selectedIds.includes(t.id))
          .map((t) => (
            <SelectOption key={t.id} value={t.id}>
              {t.nome}
            </SelectOption>
          ))}
      </Select>
      {selectedTags.map((t) => (
        <TagBadge
          key={t.id}
          tag={t}
          size="sm"
          removable
          onRemove={() => toggle(t.id)}
        />
      ))}
    </div>
  );
}
